import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlaskConical,
  Plus,
  RefreshCw,
  Filter,
  BarChart3,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import abTestService, { ABTest } from '../../services/abTestService';
import ABTestCard from './ABTestCard';
import ABTestModal from './ABTestModal';
import ABTestDetailModal from './ABTestDetailModal';
import Button from '../ui/Button';
import Select from '../ui/Select';

interface ABTestListProps {
  landingPageId?: string;
  landingPageName?: string;
  showCreateButton?: boolean;
}

const ABTestList: React.FC<ABTestListProps> = ({
  landingPageId,
  landingPageName,
  showCreateButton = true
}) => {
  const { t } = useTranslation();
  const [tests, setTests] = useState<ABTest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [editingTest, setEditingTest] = useState<ABTest | null>(null);

  const loadTests = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = landingPageId
        ? await abTestService.getTestsForPage(landingPageId, { status: statusFilter || undefined })
        : await abTestService.getAllTests({ status: statusFilter || undefined });

      if (response.success && response.data) {
        setTests(response.data);
      }
    } catch (error) {
      console.error('Error loading tests:', error);
      toast.error('Error loading tests');
    } finally {
      setIsLoading(false);
    }
  }, [landingPageId, statusFilter]);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  const handleStartTest = async (testId: string) => {
    try {
      const response = await abTestService.updateTest(testId, { status: 'running' });
      if (response.success) {
        toast.success('Test started!');
        loadTests();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error starting test');
    }
  };

  const handlePauseTest = async (testId: string) => {
    try {
      const response = await abTestService.updateTest(testId, { status: 'paused' });
      if (response.success) {
        toast.success('Test paused');
        loadTests();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error pausing test');
    }
  };

  const handleEndTest = async (testId: string) => {
    if (!window.confirm('Tem certeza que deseja encerrar este teste? Esta acao nao pode ser desfeita.')) {
      return;
    }

    try {
      const response = await abTestService.endTest(testId);
      if (response.success) {
        toast.success('Test ended!');
        loadTests();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error ending test');
    }
  };

  const handleDeleteTest = async (testId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este teste?')) {
      return;
    }

    try {
      const response = await abTestService.deleteTest(testId);
      if (response.success) {
        toast.success('Test deleted');
        loadTests();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error deleting test');
    }
  };

  const statusOptions = [
    { value: '', label: t('abTesting.filters.allStatus', 'Todos os status') },
    { value: 'draft', label: t('abTesting.status.draft', 'Rascunho') },
    { value: 'running', label: t('abTesting.status.running', 'Em andamento') },
    { value: 'paused', label: t('abTesting.status.paused', 'Pausado') },
    { value: 'completed', label: t('abTesting.status.completed', 'Concluido') }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
            <FlaskConical className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('abTesting.title', 'Testes A/B')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('abTesting.subtitle', 'Compare versoes e otimize conversoes')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={loadTests}
            leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
          >
            {t('common.refresh', 'Atualizar')}
          </Button>
          {showCreateButton && (
            <Button
              size="sm"
              onClick={() => setShowCreateModal(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              {t('abTesting.createTest', 'Criar Teste')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="w-full sm:w-48">
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Tests List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : tests.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <FlaskConical className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('abTesting.noTests', 'Nenhum teste A/B')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('abTesting.noTestsDesc', 'Crie um teste para comparar diferentes versoes da sua landing page')}
          </p>
          {showCreateButton && (
            <Button onClick={() => setShowCreateModal(true)} leftIcon={<Plus className="w-4 h-4" />}>
              {t('abTesting.createTest', 'Criar Teste')}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tests.map((test) => (
            <ABTestCard
              key={test.id}
              test={test}
              onStart={handleStartTest}
              onPause={handlePauseTest}
              onEnd={handleEndTest}
              onDelete={handleDeleteTest}
              onViewDetails={(id) => setSelectedTestId(id)}
              onEdit={(test) => setEditingTest(test)}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      <ABTestModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        landingPageId={landingPageId}
        landingPageName={landingPageName}
        onTestCreated={loadTests}
      />

      {/* Edit Modal */}
      <ABTestModal
        isOpen={!!editingTest}
        onClose={() => setEditingTest(null)}
        editTest={editingTest}
        onTestUpdated={loadTests}
      />

      {/* Detail Modal */}
      {selectedTestId && (
        <ABTestDetailModal
          isOpen={!!selectedTestId}
          onClose={() => setSelectedTestId(null)}
          testId={selectedTestId}
          onUpdate={loadTests}
        />
      )}
    </div>
  );
};

export default ABTestList;
